import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import type { ProductAttributeSelectorProps } from '../../config.types';
import { useGetAttributeDataForSelector } from '../hooks/getAttributeDataForSelector';
import { Flex, TextV2 } from '@withjoy/joykit';
import React from 'react';
import { ValuePropLineItem } from '@apps/card/routes/DigitalCardCustomizer/steps/DigitalCardReview/components/DigitalDeliveryMethodSelector/DigitalDeliveryMethodSelector.styles';
import { setAttributeStateWithValidation } from '../../utils/state/setAttributeStateWithValidation';
import { useProductAttribute } from '../../useProductAttribute';
import { useTranslation } from '@shared/core';

export const DigitalDeliveryAttribute = (props: ProductAttributeSelectorProps) => {
  const { product, attributeId, priceCalculator } = props;
  const data = useGetAttributeDataForSelector(props);
  const formatPrice = useMinorUnitsToPriceString();
  const attribute = useProductAttribute(attributeId);
  const { tq } = useTranslation('stationery');

  if (!data || !data.options.selected) {
    return null;
  }

  const uiConfig = data.attribute.attributeUIConfig;
  const quantity = Number(data.draftData.digitalCustomizations?.quantity) ?? 100;

  const options = data.options.uiConfigurations;
  const selectedOptionId = data.options.selected.optionId;

  return (
    <Flex
      flexDirection="column"
      rowGap={6}
    >
      <TextV2 typographyVariant="hed3">{uiConfig.title}</TextV2>
      {uiConfig.subtitle && <TextV2 typographyVariant="body1">{uiConfig.subtitle}</TextV2>}
      <Flex
        flexDirection="column"
        rowGap={5}
      >
        {options.map(option => {
          const isSelected = option.optionId === selectedOptionId;
          const optionPrice = priceCalculator.getOptionPrice(product.id, { attributeId, optionId: option.optionId }, quantity);
          const formattedOptionPrice = formatPrice(optionPrice, { canShowFreeLabel: true, freeLabel: tq`Free`, precision: 0 });
          const valueProps = attribute?.options.find(opt => opt.optionId === option.optionId)?.valueProps ?? [];

          return (
            <Flex
              alignItems="flex-start"
              key={option.optionId}
              paddingX={5}
              paddingY="20px"
              borderRadius={2}
              border="1px solid"
              borderColor={isSelected ? 'mono14' : 'mono3'}
              cursor="pointer"
              onClick={() => {
                setAttributeStateWithValidation(props, data, attributeId, option.optionId);
              }}
            >
              <Flex
                marginLeft={5}
                flexDirection="column"
                rowGap={3}
                flex={1}
              >
                <Flex justifyContent="space-between">
                  <TextV2 typographyVariant="button1">{option.title}</TextV2>
                  <TextV2 typographyVariant="button1">{optionPrice > 0 ? `+${formattedOptionPrice}` : formattedOptionPrice}</TextV2>
                </Flex>
                {valueProps.map(prop => (
                  <ValuePropLineItem
                    key={prop}
                    typographyVariant="label1"
                  >
                    {prop}
                  </ValuePropLineItem>
                ))}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};
